import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  CheckIcon,
  StarIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const ProgramDesignAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    visionGoals: {
      goalsDefined: data.visionGoals?.goalsDefined || 'no',
      measurableGoals: data.visionGoals?.measurableGoals || 'no',
      entryToExitJourney: data.visionGoals?.entryToExitJourney || 'no',
      notes: data.visionGoals?.notes || '',
    },
    targetAudience: {
      founderProfile: data.targetAudience?.founderProfile || 'no',
      selectionCriteria: data.targetAudience?.selectionCriteria || 'no',
      capacityAlignment: data.targetAudience?.capacityAlignment || 'no',
      notes: data.targetAudience?.notes || '',
    },
    programTypology: {
      programType: data.programTypology?.programType || '',
      stageAlignment: data.programTypology?.stageAlignment || 'no',
      designAlignment: data.programTypology?.designAlignment || 'no',
      notes: data.programTypology?.notes || '',
    },
    nationalAlignment: {
      jobCreation: data.nationalAlignment?.jobCreation || 'no',
      innovationGoals: data.nationalAlignment?.innovationGoals || 'no',
      smeDevelopment: data.nationalAlignment?.smeDevelopment || 'no',
      governmentSynergy: data.nationalAlignment?.governmentSynergy || 'no',
      privateSectorSynergy: data.nationalAlignment?.privateSectorSynergy || 'no',
      notes: data.nationalAlignment?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const programTypes = [
    'Commercialisation Program',
    'Pre-incubation Program',
    'Incubation Program',
    'Acceleration Program',
    'Capacity Building Program',
    'Training Program',
    'Other',
  ];

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const visionGoals = assessmentData.visionGoals;
    totalPoints += getPoints(visionGoals.goalsDefined);
    totalPoints += getPoints(visionGoals.measurableGoals);
    totalPoints += getPoints(visionGoals.entryToExitJourney);
    maxPoints += 3;

    const targetAudience = assessmentData.targetAudience;
    totalPoints += getPoints(targetAudience.founderProfile);
    totalPoints += getPoints(targetAudience.selectionCriteria);
    totalPoints += getPoints(targetAudience.capacityAlignment);
    maxPoints += 3;

    const programTypology = assessmentData.programTypology;
    totalPoints += programTypology.programType ? 1 : 0;
    totalPoints += getPoints(programTypology.stageAlignment);
    totalPoints += getPoints(programTypology.designAlignment);
    maxPoints += 3;

    const nationalAlignment = assessmentData.nationalAlignment;
    totalPoints += getPoints(nationalAlignment.jobCreation);
    totalPoints += getPoints(nationalAlignment.innovationGoals);
    totalPoints += getPoints(nationalAlignment.smeDevelopment);
    totalPoints += getPoints(nationalAlignment.governmentSynergy);
    totalPoints += getPoints(nationalAlignment.privateSectorSynergy);
    maxPoints += 5;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <BuildingOffice2Icon className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Program Design & Structure</h2>
            <p className="text-gray-600">
              Assess the foundational design elements and structural alignment of your program
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <CheckIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">Vision & Goals</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are the program's goals clearly defined and measurable?
            </label>
            <p className="text-xs text-gray-600">
              Goals should be specific, measurable, achievable, relevant, and time-bound (SMART)
            </p>
            <YesPartialNoRadio
              name="goalsDefined"
              value={assessmentData.visionGoals.goalsDefined}
              onChange={value => updateField('visionGoals', 'goalsDefined', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program articulate an Entry-to-Exit entrepreneur journey?
            </label>
            <p className="text-xs text-gray-600">
              Clear pathway from program entry through to successful exit (commercialisation,
              growth, or sustainability)
            </p>
            <YesPartialNoRadio
              name="measurableGoals"
              value={assessmentData.visionGoals.measurableGoals}
              onChange={value => updateField('visionGoals', 'measurableGoals', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are success metrics and milestones clearly defined?
            </label>
            <p className="text-xs text-gray-600">
              Specific indicators for measuring progress and success at each stage
            </p>
            <YesPartialNoRadio
              name="entryToExitJourney"
              value={assessmentData.visionGoals.entryToExitJourney}
              onChange={value => updateField('visionGoals', 'entryToExitJourney', value)}
            />
          </div>

          <div>
            <label
              htmlFor="visionGoalsNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="visionGoalsNotes"
              value={assessmentData.visionGoals.notes}
              onChange={e => updateField('visionGoals', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-900">Target Audience Fit</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program have a clear founder profile/persona it aims to support?
            </label>
            <p className="text-xs text-gray-600">
              Well-defined target entrepreneur characteristics, background, and stage
            </p>
            <YesPartialNoRadio
              name="founderProfile"
              value={assessmentData.targetAudience.founderProfile}
              onChange={value => updateField('targetAudience', 'founderProfile', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are selection criteria aligned to capacity and intended outcomes?
            </label>
            <p className="text-xs text-gray-600">
              Selection process matches program capacity and expected outcomes
            </p>
            <YesPartialNoRadio
              name="selectionCriteria"
              value={assessmentData.targetAudience.selectionCriteria}
              onChange={value => updateField('targetAudience', 'selectionCriteria', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there evidence of successful target audience engagement?
            </label>
            <p className="text-xs text-gray-600">
              Demonstrated ability to attract and engage the intended founder profile
            </p>
            <YesPartialNoRadio
              name="capacityAlignment"
              value={assessmentData.targetAudience.capacityAlignment}
              onChange={value => updateField('targetAudience', 'capacityAlignment', value)}
            />
          </div>

          <div>
            <label
              htmlFor="targetAudienceNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="targetAudienceNotes"
              value={assessmentData.targetAudience.notes}
              onChange={e => updateField('targetAudience', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <StarIcon className="h-6 w-6 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-900">Program Typology</h3>
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="programType" className="mb-2 block text-sm font-medium text-gray-700">
              What type of program is this?
            </label>
            <select
              id="programType"
              value={assessmentData.programTypology.programType}
              onChange={e => updateField('programTypology', 'programType', e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="">Select program type...</option>
              {programTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the program design aligned with the specific stage of the entrepreneurs it serves?
            </label>
            <p className="text-xs text-gray-600">
              Program content and delivery methods match the development stage of target
              entrepreneurs
            </p>
            <YesPartialNoRadio
              name="stageAlignment"
              value={assessmentData.programTypology.stageAlignment}
              onChange={value => updateField('programTypology', 'stageAlignment', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program structure support the intended outcomes?
            </label>
            <p className="text-xs text-gray-600">
              Program format, duration, and intensity align with expected results
            </p>
            <YesPartialNoRadio
              name="designAlignment"
              value={assessmentData.programTypology.designAlignment}
              onChange={value => updateField('programTypology', 'designAlignment', value)}
            />
          </div>

          <div>
            <label
              htmlFor="programTypologyNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="programTypologyNotes"
              value={assessmentData.programTypology.notes}
              onChange={e => updateField('programTypology', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <FlagIcon className="h-6 w-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-gray-900">
            Alignment with National Priorities
          </h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the program aligned with job creation goals?
            </label>
            <p className="text-xs text-gray-600">
              Program contributes to employment generation and economic growth
            </p>
            <YesPartialNoRadio
              name="jobCreation"
              value={assessmentData.nationalAlignment.jobCreation}
              onChange={value => updateField('nationalAlignment', 'jobCreation', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the program aligned with innovation and technology advancement goals?
            </label>
            <p className="text-xs text-gray-600">
              Program promotes innovation, technology transfer, and knowledge-based economy
            </p>
            <YesPartialNoRadio
              name="innovationGoals"
              value={assessmentData.nationalAlignment.innovationGoals}
              onChange={value => updateField('nationalAlignment', 'innovationGoals', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the program aligned with SME development goals?
            </label>
            <p className="text-xs text-gray-600">
              Program supports small and medium enterprise growth and competitiveness
            </p>
            <YesPartialNoRadio
              name="smeDevelopment"
              value={assessmentData.nationalAlignment.smeDevelopment}
              onChange={value => updateField('nationalAlignment', 'smeDevelopment', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there synergy with government mandates and policies?
            </label>
            <p className="text-xs text-gray-600">
              Program aligns with national development plans and government priorities
            </p>
            <YesPartialNoRadio
              name="governmentSynergy"
              value={assessmentData.nationalAlignment.governmentSynergy}
              onChange={value => updateField('nationalAlignment', 'governmentSynergy', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there synergy with private sector mandates and initiatives?
            </label>
            <p className="text-xs text-gray-600">
              Program collaborates with private sector partners and aligns with market needs
            </p>
            <YesPartialNoRadio
              name="privateSectorSynergy"
              value={assessmentData.nationalAlignment.privateSectorSynergy}
              onChange={value => updateField('nationalAlignment', 'privateSectorSynergy', value)}
            />
          </div>

          <div>
            <label
              htmlFor="nationalAlignmentNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="nationalAlignmentNotes"
              value={assessmentData.nationalAlignment.notes}
              onChange={e => updateField('nationalAlignment', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default ProgramDesignAssessment;
